<template>
  <div>
    <base-header type="gradient-success" class="pb-6 pb-8 pt-5 pt-md-8"></base-header>

    <div class="container-fluid mt--7">
      <div class="row">
        <div class="col">
          <div class="card shadow">
            <div class="card-header border-0">
              <div class="row align-items-center">
                <div class="col">
                  <h3 class="mb-0">Hashtag Management</h3>
                </div>
                <div class="col text-right">
                  <div class="form-inline justify-content-end">
                    <div class="form-group mr-3">
                      <label for="daysFilter" class="mr-2">Show hashtags from last:</label>
                      <select 
                        id="daysFilter" 
                        class="form-control" 
                        v-model="daysFilter"
                        @change="loadHashtags"
                      >
                        <option value="7">7 days</option>
                        <option value="14">14 days</option>
                        <option value="30">30 days</option>
                        <option value="90">90 days</option>
                        <option value="0">All time</option>
                      </select>
                    </div>
                    <base-button size="sm" @click="loadHashtags" type="primary">
                      <i class="ni ni-refresh-01 mr-2"></i>Refresh
                    </base-button>
                  </div>
                </div>
              </div>
            </div>

            <div class="table-responsive">
              <base-table
                :data="filteredHashtags"
                :columns="columns"
                thead-classes="thead-light"
                :show-pagination="true"
                :pagination-options="{ perPage: 15 }"
              >
                <template slot="columns">
                  <th>Hashtag</th>
                  <th>Usage Count</th>
                  <th>Created</th>
                  <th>Last Used</th>
                  <th>Status</th>
                  <th>Actions</th>
                </template>

                <template slot-scope="{ row }">
                  <td>
                    <span class="font-weight-bold">
                      #{{ row.hashtag_text }}
                    </span>
                  </td>
                  <td>{{ row.usage_count }}</td>
                  <td>{{ formatDate(row.created_at) }}</td>
                  <td>{{ formatDate(row.updated_at) }}</td>
                  <td>
                    <badge 
                      :type="row.status === 'approved' ? 'success' : 'warning'"
                      class="badge-pill"
                    >
                      {{ row.status }}
                    </badge>
                  </td>
                  <td>
                    <div class="d-flex">
                      <base-button
                        size="sm"
                        type="danger"
                        class="mr-2"
                        @click="deleteHashtag(row)"
                      >
                        <i class="ni ni-fat-remove"></i>
                      </base-button>
                      <base-button
                        v-if="row.status !== 'approved'"
                        size="sm"
                        type="success"
                        @click="updateHashtagStatus(row, 'approved')"
                      >
                        <i class="ni ni-check-bold"></i>
                      </base-button>
                      <base-button
                        v-else
                        size="sm"
                        type="warning"
                        @click="updateHashtagStatus(row, 'hidden')"
                      >
                        <i class="ni ni-fat-delete"></i>
                      </base-button>
                    </div>
                  </td>
                </template>
              </base-table>
            </div>

            <div class="card-footer py-4" v-if="hashtags.length === 0">
              <div class="text-center text-muted">
                <p>No hashtags found for the selected period</p>
                <base-button size="sm" @click="loadHashtags" type="primary">
                  <i class="ni ni-refresh-01 mr-2"></i>Refresh
                </base-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { swal } from 'vue-swal';
import moment from 'moment';

export default {
  name: 'HashtagManagement',
  data() {
    return {
      hashtags: [],
      daysFilter: '30',
      searchQuery: '',
      columns: ['hashtag_text', 'usage_count', 'created_at', 'updated_at', 'status'],
      isLoading: false,
      auth: {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'nabnee#' + localStorage.getItem('accessToken'),
          '_id': localStorage.getItem('_id')
        }
      }
    };
  },
  computed: {
    filteredHashtags() {
      if (!this.searchQuery) return this.hashtags;
      
      return this.hashtags.filter(tag => 
        tag.hashtag_text.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    }
  },
  mounted() {
    this.loadHashtags();
  },
  methods: {
    loadHashtags() {
      this.isLoading = true;
      
      axios.post(
        process.env.VUE_APP_API_ENDPOINT + 'read_hashtag_all',
        { daysOld: this.daysFilter === '0' ? 9999 : parseInt(this.daysFilter) },
        this.auth
      )
      .then(response => {
        if (response.data.success && response.data.details) {
          this.hashtags = response.data.details;
        } else {
          this.hashtags = [];
        }
      })
      .catch(error => {
        console.error('Error loading hashtags:', error);
        this.hashtags = [];
        
        swal({
          title: 'Error',
          text: 'Failed to load hashtags. Please try again.',
          icon: 'error'
        });
      })
      .finally(() => {
        this.isLoading = false;
      });
    },
    deleteHashtag(hashtag) {
      swal({
        title: 'Delete Hashtag',
        text: `Are you sure you want to delete #${hashtag.hashtag_text}?`,
        icon: 'warning',
        buttons: {
          cancel: {
            text: 'Cancel',
            visible: true
          },
          confirm: {
            text: 'Delete'
          }
        }
      }).then(confirmed => {
        if (confirmed) {
          axios.post(
            process.env.VUE_APP_API_ENDPOINT + 'delete_hashtag',
            { hashtag_id: hashtag.hashtag_id },
            this.auth
          )
          .then(response => {
            if (response.data.success) {
              // Remove from local array
              const index = this.hashtags.findIndex(h => h.hashtag_id === hashtag.hashtag_id);
              if (index !== -1) {
                this.hashtags.splice(index, 1);
              }
              
              swal({
                title: 'Success',
                text: 'Hashtag deleted successfully',
                icon: 'success',
                timer: 2000,
                buttons: false
              });
            } else {
              throw new Error('Failed to delete hashtag');
            }
          })
          .catch(error => {
            console.error('Error deleting hashtag:', error);
            
            swal({
              title: 'Error',
              text: 'Failed to delete hashtag. Please try again.',
              icon: 'error'
            });
          });
        }
      });
    },
    updateHashtagStatus(hashtag, status) {
      axios.post(
        process.env.VUE_APP_API_ENDPOINT + 'update_hashtag_status',
        { 
          hashtag_id: hashtag.hashtag_id,
          status: status
        },
        this.auth
      )
      .then(response => {
        if (response.data.success) {
          // Update local array
          const index = this.hashtags.findIndex(h => h.hashtag_id === hashtag.hashtag_id);
          if (index !== -1) {
            this.hashtags[index].status = status;
          }
          
          swal({
            title: 'Success',
            text: `Hashtag status updated to ${status}`,
            icon: 'success',
            timer: 2000,
            buttons: false
          });
        } else {
          throw new Error('Failed to update hashtag status');
        }
      })
      .catch(error => {
        console.error('Error updating hashtag status:', error);
        
        swal({
          title: 'Error',
          text: 'Failed to update hashtag status. Please try again.',
          icon: 'error'
        });
      });
    },
    formatDate(dateString) {
      return moment(dateString).format('MMM D, YYYY h:mm A');
    }
  }
}
</script>

<style>
.badge-pill {
  min-width: 80px;
  text-align: center;
}
</style>